/**
 * Keywords, and the one place their rules live.
 *
 * A keyword list is entered on five screens (a clinic's defaults, a store's
 * override, the article wizard, the article editor, and the platform's own
 * clinic and store forms) and validated on four routes. Every one of them has to
 * agree on the same three answers - how many, how long, and what counts as the
 * same keyword twice - because the screen that accepts thirty-one and the route
 * that rejects it produce a save that fails after the writer has typed.
 *
 * The limits also reach the database schema: `articles.keywords` is checked at
 * `cardinality(keywords) <= 30` in migration 013, and `MAX_KEYWORDS` is that
 * number. Changing it here without changing the column is a 500 waiting to
 * happen, so the migration that changes one changes the other.
 */
import { visibleText } from './rich-format.ts';

/** Per keyword. Long enough for a Japanese long-tail phrase, short of a sentence. */
export const MAX_KEYWORD_LENGTH = 60;

/** Per list. Matches the `cardinality(keywords) <= 30` check on articles. */
export const MAX_KEYWORDS = 30;

/**
 * What separates two keywords when a person pastes a list.
 *
 * Both commas, because a Japanese keyboard produces the full-width one and a
 * spreadsheet the half-width; newlines and tabs, because that is what a paste
 * out of Excel or a document actually contains. A space is deliberately NOT a
 * separator: "ダーマペン4 ダウンタイム" is one long-tail keyword, and splitting
 * it would quietly turn every phrase into single words.
 */
const SEPARATORS = /[,、\r\n\t]+/;

/**
 * One keyword, tidied.
 *
 * Trims, and collapses runs of whitespace inside the phrase to a single space -
 * including the ideographic space (U+3000) a Japanese keyboard produces, which
 * makes "たるみ 対策" typed with it and typed with a plain space the same search
 * term. A list holding both looks like a mistake to the writer and reads as
 * emphasis to the model. Deliberately NOT unicode-normalised - NFKC would rewrite
 * "ＨＩＦＵ" to "HIFU" and half-width katakana to full-width, which is a change
 * to the clinic's own wording that nobody asked this function to make.
 */
const tidy = (keyword: string): string => keyword.trim().replace(/\s+/g, ' ');

/**
 * Split what somebody typed or pasted into candidate keywords.
 *
 * Returns raw pieces; `normalizeKeywords` is what decides which survive.
 */
export function splitKeywordInput(raw: string): string[] {
  return raw.split(SEPARATORS);
}

/**
 * The list as it should be stored: tidied, non-empty, de-duplicated, capped.
 *
 * Duplicates are dropped on exact match after tidying rather than
 * case-insensitively. Case folding is meaningless for the Japanese that most of
 * these lists are written in, and for the Latin ones "HIFU" and "hifu" are
 * plausibly two different target phrases in a search console report. Order is
 * the order they were entered, because the first keyword is the one the wizard
 * uses as the working title.
 *
 * Over-long keywords are dropped, not truncated: a silently halved phrase is a
 * keyword the clinic never chose. The caller enforces the length at the input,
 * so reaching this is a paste, and dropping is the honest answer.
 */
export function normalizeKeywords(input: Iterable<string>): string[] {
  const kept: string[] = [];
  const seen = new Set<string>();
  for (const candidate of input) {
    const keyword = tidy(candidate);
    if (keyword === '' || keyword.length > MAX_KEYWORD_LENGTH) continue;
    if (seen.has(keyword)) continue;
    seen.add(keyword);
    kept.push(keyword);
    if (kept.length === MAX_KEYWORDS) break;
  }
  return kept;
}

/**
 * Two lists as one, without saying anything twice.
 *
 * The prompt sends an article's own keywords together with the ones inherited
 * from the store or clinic, and those overlap constantly - a clinic whose
 * default keyword is「たるみ」and an article about たるみ is the normal case, not
 * a mistake. Sent as-is the model receives 「たるみ、ハイフ、たるみ」, which reads
 * as emphasis it was never meant to act on.
 */
export function mergeKeywords(...lists: Array<readonly string[]>): string[] {
  return normalizeKeywords(lists.flat());
}

/**
 * Whether an article actually contains a keyword, and how many times.
 *
 * THE BUG THIS EXISTS FOR. The editorial panel counted `body.indexOf(keyword)`
 * against the STORED body, and told writers 「狙うキーワードが本文にありません」
 * about articles whose every section was about that keyword. Three separate
 * reasons, all of them the check's fault rather than the article's:
 *
 *   THE MARKUP. A stored body is Markdown plus literal HTML, so a keyword the
 *   editor bolded, linked or split across a callout - 「ハイフ<strong>治療</strong>」
 *   - is not a substring of it. The words are `visibleText(body)`; the tags are
 *   not part of what a reader or a search engine sees.
 *
 *   THE WIDTH AND THE CASE. 「ＨＩＦＵ」 typed on a Japanese keyboard, 「HIFU」 in
 *   the keyword box, 「hifu」 in a heading: the same term, three byte sequences.
 *   NFKC and a lowercase fold make them one. Note this is the OPPOSITE decision
 *   from `tidy` above, deliberately: a clinic's own keyword list must keep the
 *   wording somebody chose, and a check about whether a word appears must not
 *   care about the shape it appears in.
 *
 *   THE FRAMING. 「ダーマペン4 ダウンタイム」 is one long-tail keyword and an
 *   article about it says 「ダーマペン4のダウンタイム」 - the phrase is there, the
 *   substring is not. A check that calls that missing is a check that asks the
 *   writer to write worse Japanese, which is why `parts` is reported separately
 *   from `hits` rather than being folded into it.
 */
export interface KeywordPresence {
  /** Times the whole phrase appears, contiguously, after normalisation. */
  hits: number;
  /**
   * How much of a multi-word phrase is present when the whole is not.
   *
   * 'all' - every word appears somewhere, which is the 「framed differently」
   * case and is not a defect. 'some'/'none' for the rest. Always 'all' when
   * `hits` is above zero, and for a single-word keyword there is nothing else
   * it can be.
   */
  parts: 'all' | 'some' | 'none';
}

/**
 * What two pieces of text have to be reduced to before they are compared.
 *
 * Whitespace goes entirely: Japanese does not use it between words, so a space
 * inside a keyword is a separator the writer typed rather than something the
 * article has to reproduce. Nothing else is stripped - 「ー」 and 「・」 are part
 * of the words they appear in (ダーマペン, ヒアルロン酸・注入), and removing them
 * would make different terms compare equal.
 */
const fold = (text: string): string => text.normalize('NFKC').toLowerCase().replace(/\s+/g, '');

function occurrences(haystack: string, needle: string): number {
  if (needle === '') return 0;
  let found = 0;
  let at = haystack.indexOf(needle);
  while (at !== -1) {
    found += 1;
    at = haystack.indexOf(needle, at + needle.length);
  }
  return found;
}

/**
 * Find one keyword in one article body.
 *
 * `body` is the stored body, markup and all - the stripping happens here so no
 * caller has to remember to do it.
 */
export function keywordPresence(body: string, keyword: string): KeywordPresence {
  const phrase = tidy(keyword);
  if (phrase === '') return { hits: 0, parts: 'none' };

  const text = fold(visibleText(body));
  const hits = occurrences(text, fold(phrase));
  if (hits > 0) return { hits, parts: 'all' };

  const words = phrase.split(' ').filter((word) => word !== '');
  if (words.length < 2) return { hits: 0, parts: 'none' };

  const present = words.filter((word) => text.includes(fold(word))).length;
  return {
    hits: 0,
    parts: present === words.length ? 'all' : present > 0 ? 'some' : 'none',
  };
}
